import React from 'react';
import Inputfield from './inputfields'; // Assuming Inputfield is a generic input component

class OrderCatalogueModal extends React.Component {
    constructor(props) {
        super(props);
        this.modalRef = React.createRef();
        this.bsModal = null;

        this.initializeItems = (data) => {
            const flattened = [];
            data.groups && data.groups.forEach((outer, ind1) => {
                outer.items.forEach((inner, ind2) => {
                    flattened.push({
                        data: inner,
                        isChecked: false,
                        id: `${ind1}-${ind2}-${inner.productName}-${inner.attributes ? inner.attributes.name : ''}`, // Unique ID
                        label: `${inner.productName} - ${inner.attributes ? inner.attributes.name : ''}`,
                        isEmptyField: !(inner.productName && inner.attributes && inner.attributes.name),
                    });
                });
            });
            return flattened;
        };

        const columnsPerRow = props.modalData.numItems || 1; // Ensure columnsPerRow is defined
        this.state = {
            items: this.initializeItems(props.modalData),
            selectAll: false,
            // New: State to track column-level select all checkboxes
            isColumnSelected: Array(columnsPerRow).fill(false),
        };
    }

    componentDidMount() {
        if (window.bootstrap && this.modalRef.current) {
            this.bsModal = new window.bootstrap.Modal(this.modalRef.current, {
                backdrop: 'static',
                keyboard: false
            });

            this.modalRef.current.addEventListener('hidden.bs.modal', this.handleBsModalHidden);
            this.modalRef.current.addEventListener('shown.bs.modal', this.handleBsModalShown);

            if (this.props.isOpen) {
                this.bsModal.show();
            }
        }
        // Initial check for column select all states after data initialization
        this.updateColumnSelectAllStates(this.state.items);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.bsModal) {
            if (this.props.isOpen && !prevProps.isOpen) {
                this.bsModal.show();
            } else if (!this.props.isOpen && prevProps.isOpen) {
                this.bsModal.hide();
            }
        }

        if (prevProps.modalData !== this.props.modalData) {
            const newItems = this.initializeItems(this.props.modalData);
            const columnsPerRow = this.props.modalData.numItems || 1;
            this.setState({
                items: newItems,
                selectAll: false, // Reset select all when data changes
                isColumnSelected: Array(columnsPerRow).fill(false), // Reset column select all
            }, () => {
                // After state updates, re-evaluate column select all states
                this.updateColumnSelectAllStates(newItems);
            });
        }
        // If items changed due to individual or row selection, update column states
        if (prevState.items !== this.state.items) {
             this.updateColumnSelectAllStates(this.state.items);
        }
    }

    componentWillUnmount() {
        if (this.bsModal) {
            this.modalRef.current.removeEventListener('hidden.bs.modal', this.handleBsModalHidden);
            this.modalRef.current.removeEventListener('shown.bs.modal', this.handleBsModalShown);
            this.bsModal.dispose();
            this.bsModal = null;
        }
    }

    handleBsModalHidden = () => {
        if (this.props.isOpen) {
            this.props.onClose();
        }
    };

    handleBsModalShown = () => {
        console.log('Bootstrap modal is fully shown');
    };

    handleSubmitButtonClick = () => {
        const selectedItems = this.state.items
            .filter(ele => ele.isChecked && !ele.isEmptyField)
            .map(ele => ele.data);
        this.props.onClose(selectedItems);
    }

    handleCloseButton = () => {
        this.props.onClose();
    };

    // Helper to update global selectAll and column selectAll states
    updateOverallSelectionStates = (updatedItems) => {
        const allItemsChecked = updatedItems.every(item => item.isChecked || item.isEmptyField);
        this.setState({ selectAll: allItemsChecked });
        this.updateColumnSelectAllStates(updatedItems);
    };

    // Helper to determine and set column-level select all states
    updateColumnSelectAllStates = (currentItems) => {
        const columnsPerRow = this.props.modalData.numItems || 1;
        const newIsColumnSelected = Array(columnsPerRow).fill(false);

        for (let colIndex = 0; colIndex < columnsPerRow; colIndex++) {
            let isColumnFullyChecked = true;
            let hasAnyActualItemInColumn = false;

            // Iterate through all items, checking only those that fall into the current column
            for (let i = colIndex; i < currentItems.length; i += columnsPerRow) {
                const item = currentItems[i];
                if (!item.isEmptyField) {
                    hasAnyActualItemInColumn = true;
                    if (!item.isChecked) {
                        isColumnFullyChecked = false;
                        break; // No need to check further if one item is unchecked
                    }
                }
            }
            newIsColumnSelected[colIndex] = hasAnyActualItemInColumn && isColumnFullyChecked;
        }
        this.setState({ isColumnSelected: newIsColumnSelected });
    };


    // Handle individual checkbox changes
    handleItemChange = (id) => {
        this.setState(prevState => {
            const updatedItems = prevState.items.map(item =>
                item.id === id ? { ...item, isChecked: !item.isChecked } : item
            );
            return { items: updatedItems };
        }, () => this.updateOverallSelectionStates(this.state.items)); // Recalculate all select states
    };

    // Handle "Select All" checkbox changes (Master Select All)
    handleSelectAll = () => {
        this.setState(prevState => {
            const newSelectAllState = !prevState.selectAll;
            const updatedItems = prevState.items.map(item => ({
                ...item,
                isChecked: newSelectAllState && !item.isEmptyField,
            }));
            return { items: updatedItems, selectAll: newSelectAllState };
        }, () => this.updateColumnSelectAllStates(this.state.items)); // Also update column states
    };

    // New handler for row-level "Select All"
    handleRawSelectAll = (startIndex, endIndex) => {
        this.setState(prevState => {
            const currentItemsInRow = prevState.items.slice(startIndex, endIndex);
            const areAllInRowChecked = currentItemsInRow.filter(item => !item.isEmptyField).every(item => item.isChecked);
            const newStateForItemsInRow = !areAllInRowChecked; // Toggle state

            const updatedItems = prevState.items.map((item, index) => {
                if (index >= startIndex && index < endIndex && !item.isEmptyField) {
                    return { ...item, isChecked: newStateForItemsInRow };
                }
                return item;
            });
            return { items: updatedItems };
        }, () => this.updateOverallSelectionStates(this.state.items)); // Recalculate all select states
    };

    // NEW: Handler for column-level "Select All"
    handleColumnSelectAll = (colIndex) => {
        this.setState(prevState => {
            const newIsColumnSelected = [...prevState.isColumnSelected];
            const currentColumnState = newIsColumnSelected[colIndex];
            const newStateForColumn = !currentColumnState;

            const updatedItems = prevState.items.map((item, index) => {
                // Check if this item belongs to the current column
                // An item at 'index' belongs to 'colIndex' if (index % columnsPerRow === colIndex)
                if ((index % (this.props.modalData.numItems || 1)) === colIndex && !item.isEmptyField) {
                    return { ...item, isChecked: newStateForColumn };
                }
                return item;
            });

            // Update the specific column's select all state
            newIsColumnSelected[colIndex] = newStateForColumn;

            return {
                items: updatedItems,
                isColumnSelected: newIsColumnSelected
            };
        }, () => this.updateOverallSelectionStates(this.state.items)); // Recalculate master selectAll and other column states
    };


    renderRows = () => {
        const rows = [];
        const { items } = this.state;
        const columnsPerRow = this.props.modalData.numItems || 1; // Ensure default value

        if (!items || items.length === 0) {
            return (
                <tr>
                    <td colSpan={columnsPerRow + 1} className="text-center p-3">No items available.</td>
                </tr>
            );
        }

        for (let i = 0; i < items.length; i += columnsPerRow) {
            const rowItems = items.slice(i, i + columnsPerRow);

            const actualItemsInRow = rowItems.filter(item => !item.isEmptyField);
            // A row's select all should be checked if all its *actual* items are checked
            const isRowSelected = actualItemsInRow.length > 0 && actualItemsInRow.every(item => item.isChecked);

            rows.push(
                <tr key={`row-${i}`}> {/* Use 'i' as key, which is unique for each row start */}
                    <td className='border-radius-0 p-2 w-25'>
                        <Inputfield
                            handleChange={() => this.handleRawSelectAll(i, i + columnsPerRow)}
                            type="checkbox"
                            name={`row-select-all-${i}`}
                            checked={isRowSelected}
                            className="m-2"
                        />
                    </td>
                    {rowItems.map((item, colIndex) => ( // Use colIndex for mapping individual cells within a row
                        <React.Fragment key={item.id || `empty-item-${i}-${colIndex}`}>
                            {!item.isEmptyField ? (
                                <td className='border-radius-0 p-2'>
                                    <Inputfield
                                        handleChange={() => this.handleItemChange(item.id)}
                                        type="checkbox"
                                        name={item.id}
                                        checked={item.isChecked}
                                        className="m-2"
                                    />
                                    {item.label}
                                </td>
                            ) : (
                                <td className='border-radius-0 p-2'></td> // Render empty td for empty fields
                            )}
                        </React.Fragment>
                    ))}
                </tr>
            );
        }
        return rows;
    };

    render() {
        const { selectAll, isColumnSelected } = this.state;
        const { modalData } = this.props;
        const columnsPerRow = modalData.numItems || 1; // Ensure columnsPerRow is defined

        // Create an array of column headers for mapping
        const columnHeaders = Array.from({ length: columnsPerRow }).map((_, colIndex) => (
            <th key={`col-header-${colIndex}`} className='border-radius-0 p-2'>
                <Inputfield
                    handleChange={() => this.handleColumnSelectAll(colIndex)}
                    type="checkbox"
                    name={`col-select-all-${colIndex}`}
                    checked={isColumnSelected[colIndex]} // Use column-specific state
                    className="m-2"
                />
                <span className="ml-2">Column {colIndex + 1}</span> {/* Dynamic column header */}
            </th>
        ));

        return (
            <div
                className="modal fade"
                id="OrdercatalogueModal"
                tabIndex="-1"
                role="dialog"
                aria-labelledby="myLargeModalLabel"
                aria-hidden={!this.props.isOpen}
                ref={this.modalRef}
            >
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="myLargeModalLabel">
                                Catalogue ({modalData.name})
                            </h5>
                            <button
                                type="button"
                                className="close cursor-pointer"
                                data-dismiss="modal"
                                aria-label="Close"
                                onClick={this.handleCloseButton}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className='row'>
                                <div className='col-12'>
                                    <table className="table table-bordered order-list-data">
                                        <thead>
                                            <tr>
                                                <th className='border-radius-0 p-2 w-25'> {/* Master Select All Column */}
                                                    <Inputfield
                                                        handleChange={this.handleSelectAll}
                                                        type="checkbox"
                                                        name="group-all"
                                                        checked={selectAll}
                                                        className="m-2"
                                                    />
                                                    <span className="ml-2">Select All</span>
                                                </th>
                                                {columnHeaders} {/* Render the dynamic column headers here */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.renderRows()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer bg-white text-center justify-content-center">
                            <button type="button" className="btn btn-primary"
                                onClick={this.handleSubmitButtonClick}>
                                Submit
                            </button>
                            <button type="button" className="btn btn-secondary"
                                onClick={this.handleCloseButton}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}
export default OrderCatalogueModal;